import { API_BASE } from '../config';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../App';
import { useTranslation } from '../i18n/LanguageContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area, Legend } from 'recharts';
import ModernPageLayout, { ModernCard, ModernBadge, colorPalette } from './ModernPageLayout';
import LanguageSelector from './LanguageSelector';
import { BarChart3, RefreshCw, Users, Clock, CheckCircle, TrendingUp, Building, Calendar, AlertCircle, Home, PanelRightOpen, X, Download, Image, FileSpreadsheet, FileText } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

// Hook Dashboard connecté aux vraies données (role: 'employee' => API employee-scoped).
// For admin/manager: filterType 'employee'|'department', filterValue = employee id or department name.
const useDashboard = (refreshInterval = 30000, role = null, filterType = 'employee', filterValue = '') => {
  const isEmployee = role === 'employee';
  const [dashboardData, setDashboardData] = useState({
    overview: {
      totalEmployees: 0,
      activeEmployees: 0,
      presentToday: 0,
      avgHoursPerDay: 0,
      totalDepartments: 0,
      onTimeToday: 0,
      avgArrivalTime: 9.0
    },
    weeklyStats: [],
    departmentStats: [],
    attendanceToday: [],
    monthlyTrends: [],
    leaveStats: [],
    lateArrivals: [],
    ongoingLeaves: 0,
    alerts: []
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // ✅ Utiliser API_BASE depuis config.js (http://localhost:3001 en dev)
  // const API_BASE = '/api'; // ❌ ERREUR : cela pointe vers port 3000
  
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    const userTimezone = typeof Intl !== 'undefined' && Intl.DateTimeFormat ? Intl.DateTimeFormat().resolvedOptions().timeZone : '';
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    if (userTimezone) headers['X-User-Timezone'] = userTimezone;
    return headers;
  };

  const loadDashboardData = async () => {
    try {
      setError(null);
      let url = isEmployee ? `${API_BASE}/api/dashboard/employee` : `${API_BASE}/api/dashboard/complete`;
      if (!isEmployee && filterType && filterValue) {
        const params = new URLSearchParams();
        if (filterType === 'employee') params.set('employee_id', String(filterValue));
        else if (filterType === 'department') params.set('department', String(filterValue));
        if (params.toString()) url += '?' + params.toString();
      }
      console.log('🔄 Chargement dashboard...', isEmployee ? '(employee)' : `(admin/manager ${filterType}=${filterValue || 'all'})`);
      
      const dashboardResponse = await fetchWithTimeout(
        url,
        { method: 'GET', headers: getAuthHeaders() },
        15000
      );
      
      if (dashboardResponse.ok) {
        const dashboardResult = await dashboardResponse.json();
        console.log('✅ Dashboard data reçues:', {
          overview: dashboardResult.overview,
          weeklyStats: dashboardResult.weeklyStats?.length || 0,
          attendanceToday: dashboardResult.attendanceToday?.length || 0,
          ongoingLeaves: dashboardResult.ongoingLeaves
        });
        if (isEmployee) {
          setDashboardData(dashboardResult);
        } else {
          const enrichedData = await enrichDashboardData(dashboardResult);
          setDashboardData(enrichedData);
        }
        setLastUpdate(new Date());
      } else {
        const errorText = await dashboardResponse.text();
        let errorData = {};
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {}
        console.warn('⚠️ Dashboard API erreur:', dashboardResponse.status, errorData.error || errorText);
        setError(errorData.error || errorText || 'Dashboard unavailable');
        setDashboardData(getDefaultData());
      }
      
    } catch (err) {
      const isTimeout = err?.name === 'AbortError';
      setError(isTimeout ? 'Request timed out - check backend (port 3001)' : err.message);
      if (isTimeout) console.warn('⚠️ Dashboard: request timed out');
      else console.error('❌ Erreur chargement dashboard:', err);
      try {
        setDashboardData(getDefaultData());
      } catch (fallbackError) {
        setDashboardData(getDefaultData());
      }
    } finally {
      setLoading(false);
    }
  };

  // Charger les données de base depuis les APIs existantes
  const loadBasicData = async () => {
    try {
      console.log('📊 Chargement données de base...');
      
      // Employés (10s timeout)
      console.log('🔍 Chargement employés depuis:', `${API_BASE}/api/employees`);
      const employeesResponse = await fetchWithTimeout(
        `${API_BASE}/api/employees?all=true&active=all`,
        { headers: getAuthHeaders() },
        10000
      );
      
      let employeesData = { employees: [] };
      if (employeesResponse.ok) {
        employeesData = await employeesResponse.json();
      }
      
      // ✅ CORRECTION : Gérer la structure paginée (data.employees ou data.data)
      const employees = employeesData.employees || employeesData.data || [];
      console.log('👥 Employés chargés:', employees.length);
      
      // Statistiques de base
      const totalEmployees = employees.length;
      const employeesWithAccount = employees.filter(emp => emp.hasAccount || emp.user_id).length;
      const departments = [...new Set(employees.map(emp => emp.department).filter(Boolean))];
      
      // 🚀 CHARGER LES VRAIES DONNÉES DEPUIS L'API ATTENDANCE
      let presentToday = 0;
      let onTimeToday = 0;
      let lateToday = 0;
      let absentToday = totalEmployees;
      
      try {
        const attendanceResponse = await fetchWithTimeout(
          `${API_BASE}/api/attendance/status`,
          { headers: getAuthHeaders() },
          10000
        );
        
        if (attendanceResponse.ok) {
          const attendanceData = await attendanceResponse.json();
          console.log('📊 Données présence réelles:', attendanceData);
          
          // Calculer depuis les vraies données de pointage
          const today = new Date().toISOString().split('T')[0];
          const todayRecords = (attendanceData.records || []).filter(record => {
            const recordDate = new Date(record.timestamp).toISOString().split('T')[0];
            return recordDate === today && record.clock_type === 'in';
          });
          
          presentToday = new Set(todayRecords.map(r => r.employee_id)).size;
          onTimeToday = todayRecords.filter(r => r.time && r.time <= '09:00:00').length;
          lateToday = presentToday - onTimeToday;
          absentToday = totalEmployees - presentToday;
          
          console.log('✅ Présence calculée depuis vraies données:', {
            presentToday,
            onTimeToday,
            lateToday,
            absentToday
          });
        } else {
          console.warn('⚠️ Impossible de charger les données de présence, utilisation données simulées');
          // Fallback sur données simulées si API non disponible
          presentToday = Math.floor(totalEmployees * 0.7);
          onTimeToday = Math.floor(presentToday * 0.8);
          lateToday = presentToday - onTimeToday;
          absentToday = totalEmployees - presentToday;
        }
      } catch (attendanceError) {
        console.error('❌ Erreur chargement présence:', attendanceError);
        // Fallback sur données simulées
        presentToday = Math.floor(totalEmployees * 0.7);
        onTimeToday = Math.floor(presentToday * 0.8);
        lateToday = presentToday - onTimeToday;
        absentToday = totalEmployees - presentToday;
      }
      
      return {
        overview: {
          totalEmployees,
          activeEmployees: employeesWithAccount,
          presentToday,
          avgHoursPerDay: 7.5,
          totalDepartments: departments.length,
          onTimeToday,
          avgArrivalTime: 8.75
        },
        weeklyStats: generateWeeklyStats(totalEmployees),
        departmentStats: generateDepartmentStats(employees, departments),
        attendanceToday: [
          { name: 'on_time', value: onTimeToday, color: '#10B981' },
          { name: 'late', value: lateToday, color: '#F59E0B' },
          { name: 'absent', value: absentToday, color: '#EF4444' }
        ],
        monthlyTrends: generateMonthlyTrends(),
        leaveStats: [], // Leave stats are real-time from API only; never use fake data here
        lateArrivals: generateLateArrivals(employees),
        alerts: [
          {
            type: 'info',
            title: 'simulated_data',
            message: 'simulated_data_desc',
            icon: 'ℹ️'
          }
        ]
      };
    } catch (error) {
      console.error('❌ Erreur chargement données de base:', error);
      return getDefaultData();
    }
  };

  // Enrichir les données avec des calculs supplémentaires
  const enrichDashboardData = async (baseData) => {
    try {
      // Si on a des données du dashboard API (avec overview et weeklyStats), les utiliser directement
      if (baseData.overview && baseData.weeklyStats && Array.isArray(baseData.weeklyStats) && baseData.weeklyStats.length > 0) {
        const ongoingLeaves = baseData.ongoingLeaves ?? (Array.isArray(baseData.leaveStats)
          ? baseData.leaveStats.reduce((sum, l) => sum + (l.approved || 0), 0) : 0);
        console.log('✅ Utilisation des données du dashboard API:', {
          overview: baseData.overview,
          weeklyStats: baseData.weeklyStats.length,
          attendanceToday: baseData.attendanceToday?.length || 0,
          ongoingLeaves
        });
        return {
          ...baseData,
          ongoingLeaves,
          alerts: [
            {
              type: 'success',
              title: 'real_time',
              message: 'real_time_desc',
              icon: '✅'
            }
          ]
        };
      }
      
      // Si pas de données valides du dashboard API, charger depuis les APIs de base
      console.warn('⚠️ Données dashboard API incomplètes, chargement depuis APIs de base');
      return await loadBasicData();
    } catch (error) {
      console.error('❌ Erreur enrichissement:', error);
      return baseData;
    }
  };

  // Générer des statistiques hebdomadaires réalistes
  const generateWeeklyStats = (totalEmployees) => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return days.map(day => {
      const isWeekend = day === 'Sat' || day === 'Sun';
      const basePresence = isWeekend ? 0.1 : 0.75;
      const present = Math.floor(totalEmployees * basePresence);
      const late = isWeekend ? 0 : Math.floor(present * 0.15);
      const absent = totalEmployees - present;
      
      return { day, present, late, absent };
    });
  };

  // Générer des statistiques par département
  const generateDepartmentStats = (employees, departments) => {
    return departments.map(dept => {
      const deptEmployees = employees.filter(emp => emp.department === dept);
      const total = deptEmployees.length;
      const present = Math.floor(total * (0.6 + Math.random() * 0.3));
      const late = Math.floor(present * 0.2);
      const attendanceRate = total > 0 ? Math.round((present / total) * 100) : 0;
      
      return {
        department: dept,
        total_employees: total,
        present_today: present,
        late_today: late,
        attendance_rate: attendanceRate,
        avg_daily_hours: 7.0 + Math.random() * 1.5
      };
    });
  };

  // Générer des tendances mensuelles
  const generateMonthlyTrends = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    return months.map(month => ({
      month,
      attendance: 80 + Math.floor(Math.random() * 15),
      productivity: 85 + Math.floor(Math.random() * 10),
      satisfaction: 88 + Math.floor(Math.random() * 8)
    }));
  };

  // Générer des statistiques de congés
  const generateLeaveStats = () => {
    return [
      { name: 'Congé payé', approved: 5, pending: 2, rejected: 0, color: '#3B82F6' },
      { name: 'Maladie', approved: 3, pending: 1, rejected: 0, color: '#EF4444' },
      { name: 'Formation', approved: 2, pending: 0, rejected: 0, color: '#10B981' },
      { name: 'RTT', approved: 4, pending: 1, rejected: 1, color: '#8B5CF6' }
    ];
  };

  // Générer des retards réalistes
  const generateLateArrivals = (employees) => {
    const lateEmployees = employees
      .filter(() => Math.random() < 0.2) // 20% de chance d'être en retard
      .slice(0, 3) // Maximum 3 retards
      .map(emp => ({
        name: `${emp.first_name} ${emp.last_name}`,
        department: emp.department || 'Non défini',
        arrival_time: `09:${String(15 + Math.floor(Math.random() * 30)).padStart(2, '0')}`,
        late_severity: Math.random() > 0.5 ? 'Léger' : 'Modéré'
      }));
    
    return lateEmployees;
  };

  // Données par défaut en cas d'erreur totale
  const getDefaultData = () => ({
    overview: {
      totalEmployees: 0,
      activeEmployees: 0,
      presentToday: 0,
      avgHoursPerDay: 0,
      totalDepartments: 0,
      onTimeToday: 0,
      avgArrivalTime: 9.0
    },
    weeklyStats: [],
    departmentStats: [],
    attendanceToday: [],
    monthlyTrends: [],
    leaveStats: [],
    lateArrivals: [],
    ongoingLeaves: 0,
    alerts: [
      {
        type: 'error',
        title: 'connection_error',
        message: 'loading_error',
        icon: '❌'
      }
    ]
  });

  const refreshDashboard = async () => {
    setLoading(true);
    await loadDashboardData();
  };

  useEffect(() => {
    loadDashboardData();
    
    const interval = setInterval(() => {
      if (!error) loadDashboardData();
    }, refreshInterval);
    
    return () => clearInterval(interval);
  }, [refreshInterval, error, isEmployee, filterType, filterValue]);

  return {
    dashboardData,
    loading,
    error,
    lastUpdate,
    refreshDashboard
  };
};

const AdminDashboard = ({ onNavigate }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { enabledFeatures = [], billingLoaded } = useOutletContext() || {};
  const hasConges = billingLoaded && enabledFeatures?.includes('conges');
  const isEmployee = user?.role === 'employee';
  const [filterType, setFilterType] = useState('employee');
  const [filterValue, setFilterValue] = useState('');
  const { dashboardData, loading, error, lastUpdate, refreshDashboard } = useDashboard(30000, user?.role, filterType, filterValue);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedView, setSelectedView] = useState('overview');
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [exportingFormat, setExportingFormat] = useState(null);
  const dashboardReportRef = useRef(null);
  const reportsSectionRef = useRef(null);

  useEffect(() => {
    if (!hasConges && selectedView === 'leaves') setSelectedView('overview');
  }, [hasConges, selectedView]);
  useEffect(() => {
    if (isEmployee && selectedView === 'departments') setSelectedView('overview');
  }, [isEmployee, selectedView]);

  // Détecter si on est sur mobile (matchMedia pour cohérence avec le CSS)
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const checkMobile = () => setIsMobile(mql.matches);
    checkMobile();
    mql.addEventListener('change', checkMobile);
    return () => mql.removeEventListener('change', checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile && mobileMenuOpen) setMobileMenuOpen(false);
  }, [isMobile, mobileMenuOpen]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshDashboard();
    setRefreshing(false);
  };

  const getReportScopeLabel = () => {
    if (isEmployee) return t('dashboard.subtitle_employee') || 'My dashboard';
    if (filterType === 'employee' && filterValue) {
      const emp = (dashboardData.filterOptions?.employees || []).find(e => String(e.id) === String(filterValue));
      return emp ? [emp.firstName || emp.first_name, emp.lastName || emp.last_name].filter(Boolean).join(' ') || `#${filterValue}` : `Employee ${filterValue}`;
    }
    if (filterType === 'department' && filterValue) return filterValue;
    return t('dashboard.filter_all_employees') || 'All';
  };

  const handleExportImage = async () => {
    if (!dashboardReportRef.current) return;
    setExportingFormat('image');
    const el = dashboardReportRef.current;
    try {
      // Scroll report area into view so layout is correct; capture full content
      el.scrollIntoView({ behavior: 'instant', block: 'start' });
      const fullHeight = el.scrollHeight || el.offsetHeight;
      const fullWidth = el.scrollWidth || el.offsetWidth;
      const scale = 2;
      const canvas = await html2canvas(el, {
        useCORS: true,
        allowTaint: true,
        scale,
        backgroundColor: '#0a0e27',
        width: fullWidth,
        height: fullHeight,
        windowWidth: fullWidth,
        windowHeight: fullHeight,
        scrollX: 0,
        scrollY: 0,
        logging: false
      });
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png', 1));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dashboard-report-${selectedView}-${new Date().toISOString().slice(0, 10)}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export image failed:', e);
    } finally {
      setExportingFormat(null);
    }
  };

  const buildReportData = () => {
    const scope = getReportScopeLabel();
    const date = new Date().toLocaleDateString(undefined, { dateStyle: 'medium' });
    const o = dashboardData.overview || {};
    const rows = [
      ['Dashboard Report', ''],
      ['Scope', scope],
      ['View', selectedView],
      ['Date', date],
      [],
      ['Overview', ''],
      ['Total Employees', o.totalEmployees ?? '—'],
      ['Present Today', o.presentToday ?? '—'],
      ['On Time Today', o.onTimeToday ?? '—'],
      ['Avg Arrival Time', o.avgArrivalTime != null ? `${Math.floor(o.avgArrivalTime)}h${String(Math.round((o.avgArrivalTime % 1) * 60)).padStart(2, '0')}` : '—'],
      ['Delays', o.lateCount ?? dashboardData.lateArrivals?.length ?? '—'],
      ['Ongoing Leaves', dashboardData.ongoingLeaves ?? '—'],
      ['GPS Attendance', o.gpsStats?.totalWithGPS ?? '—'],
      ['Avg Hours/Day', (o.avgHoursPerDay ?? 0).toFixed(1)],
      [],
      ['Weekly Attendance', ''],
      ['Day', 'Present', 'Late', 'Absent'],
      ...(dashboardData.weeklyStats || []).map(r => [r.day, r.present, r.late, r.absent]),
      [],
      ['Today Distribution', ''],
      ...(dashboardData.attendanceToday || []).map(r => [r.name, r.value]),
      [],
      ['Monthly Trends', ''],
      ['Month', 'Productivity', 'Satisfaction', 'Attendance'],
      ...(dashboardData.monthlyTrends || []).map(r => [r.month, r.productivity, r.satisfaction, r.attendance]),
    ];
    if ((dashboardData.leaveStats || []).length) {
      rows.push([], ['Leave Statistics', '']);
      rows.push(['Type', 'Approved', 'Pending', 'Rejected']);
      dashboardData.leaveStats.forEach(l => rows.push([l.name, l.approved, l.pending, l.rejected]));
    }
    if ((dashboardData.departmentStats || []).length) {
      rows.push([], ['Departments', '']);
      rows.push(['Department', 'Employees', 'Present Today', 'Late', 'Attendance %', 'Avg Hours']);
      dashboardData.departmentStats.forEach(d => rows.push([d.department, d.total_employees, d.present_today, d.late_today, d.attendance_rate, d.avg_daily_hours]));
    }
    return rows;
  };

  const handleExportExcel = () => {
    setExportingFormat('excel');
    try {
      const rows = buildReportData();
      const csv = rows.map(row => row.map(cell => (typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n')) ? `"${String(cell).replace(/"/g, '""')}"` : cell)).join(',')).join('\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dashboard-report-${selectedView}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export Excel failed:', e);
    } finally {
      setExportingFormat(null);
    }
  };

  const handleExportPdf = () => {
    setExportingFormat('pdf');
    try {
      const doc = new jsPDF();
      const scope = getReportScopeLabel();
      const date = new Date().toLocaleDateString(undefined, { dateStyle: 'medium' });
      let y = 15;
      doc.setFontSize(16);
      doc.text('Dashboard Report', 14, y); y += 8;
      doc.setFontSize(10);
      doc.text(`Scope: ${scope}  |  View: ${selectedView}  |  Date: ${date}`, 14, y); y += 10;
      const rows = buildReportData();
      doc.setFontSize(9);
      rows.forEach(row => {
        if (row.length === 0) { y += 4; return; }
        const line = row.join(' — ');
        if (y > 270) { doc.addPage(); y = 15; }
        doc.text(line, 14, y);
        y += 6;
      });
      doc.save(`dashboard-report-${selectedView}-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (e) {
      console.error('Export PDF failed:', e);
    } finally {
      setExportingFormat(null);
    }
  };

  // Styles responsives intégrés
  const mobileStyles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: isMobile ? '10px' : '2rem 1rem'
    },
    wrapper: {
      maxWidth: '1400px',
      margin: '0 auto',
      width: '100%'
    },
    header: {
      background: 'rgba(255,255,255,0.95)',
      borderRadius: isMobile ? '15px' : '20px',
      padding: isMobile ? '15px' : '2rem',
      marginBottom: isMobile ? '15px' : '2rem',
      backdropFilter: 'blur(20px)',
      boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
    },
    headerContent: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: isMobile ? '10px' : '1rem',
      marginBottom: isMobile ? '15px' : '2rem'
    },
    title: {
      fontSize: isMobile ? '1.8rem' : '2.5rem',
      fontWeight: 'bold',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      margin: '0 0 0.5rem 0',
      textAlign: isMobile ? 'center' : 'left'
    },
    subtitle: {
      color: '#6b7280',
      fontSize: isMobile ? '1rem' : '1.1rem',
      margin: 0,
      textAlign: isMobile ? 'center' : 'left'
    },
    controls: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: 'center',
      gap: isMobile ? '8px' : '1rem'
    },
    refreshButton: {
      padding: isMobile ? '8px 15px' : '0.75rem 1.5rem',
      borderRadius: '50px',
      border: 'none',
      background: refreshing 
        ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)' 
        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      fontWeight: '600',
      cursor: refreshing ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.3s ease',
      transform: refreshing ? 'scale(0.95)' : 'scale(1)',
      boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
      fontSize: isMobile ? '0.8rem' : '0.875rem'
    },
    tabsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: isMobile ? '4px' : '1rem',
      borderBottom: '2px solid #f3f4f6',
      paddingBottom: '1rem',
      justifyContent: isMobile ? 'center' : 'flex-start'
    },
    tab: (isActive) => ({
      padding: isMobile ? '6px 12px' : '0.75rem 1.5rem',
      borderRadius: '50px',
      border: 'none',
      background: isActive 
        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
        : 'transparent',
      color: isActive ? 'white' : '#6b7280',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      transform: isActive ? 'scale(1.05)' : 'scale(1)',
      boxShadow: isActive ? '0 4px 20px rgba(102, 126, 234, 0.4)' : 'none',
      fontSize: isMobile ? '0.7rem' : '0.875rem'
    }),
    statsGrid: {
      display: isMobile ? 'flex' : 'grid',
      flexDirection: isMobile ? 'column' : undefined,
      gridTemplateColumns: isMobile ? undefined : 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: isMobile ? '10px' : '1.5rem',
      marginBottom: isMobile ? '15px' : '2rem'
    },
    statCard: (gradient, shadowColor) => ({
      background: gradient,
      borderRadius: isMobile ? '15px' : '20px',
      padding: isMobile ? '15px' : '2rem',
      color: 'white',
      transform: 'scale(1)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      boxShadow: `0 10px 30px ${shadowColor}`,
      position: 'relative',
      overflow: 'hidden',
      width: '100%'
    }),
    chartsContainer: {
      display: isMobile ? 'flex' : 'grid',
      flexDirection: isMobile ? 'column' : undefined,
      gridTemplateColumns: isMobile ? undefined : '1fr 1fr',
      gap: isMobile ? '15px' : '2rem',
      marginBottom: isMobile ? '15px' : '2rem'
    },
    chartCard: {
      background: 'rgba(255,255,255,0.95)',
      borderRadius: isMobile ? '15px' : '20px',
      padding: isMobile ? '15px' : '2rem',
      backdropFilter: 'blur(20px)',
      boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      boxSizing: 'border-box'
    },
    chartTitle: {
      margin: '0 0 1.5rem 0',
      fontSize: isMobile ? '1rem' : '1.25rem',
      fontWeight: 'bold',
      color: '#1f2937',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    chartWrapper: {
      width: '100%',
      height: isMobile ? '250px' : '300px',
      overflow: 'hidden',
      minWidth: 0
    },
    // Container 100% largeur sur mobile (comme la notification/alert box)
    scrollContainer: {
      width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden',
      overflowY: 'hidden',
      minWidth: 0
    },
    scrollContent: {
      width: '100%',
      minWidth: 0,
      maxWidth: '100%'
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      <style>{`
        @media (max-width: 768px) {
          .dashboard-topbar-desktop { display: none !important; }
          .dashboard-topbar-mobile { display: flex !important; }
          .dashboard-topbar-subtitle { display: none !important; }
        }
        @media (min-width: 769px) {
          .dashboard-topbar-desktop { display: flex !important; }
          .dashboard-topbar-mobile { display: none !important; }
        }
      `}</style>
      {/* Header: CSS media query forces expand icon on mobile so it's responsive without JS delay */}
      <div className="dashboard-topbar-wrap" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: '24px 20px 20px',
        background: 'transparent',
        backdropFilter: 'none',
        borderBottom: 'none',
        zIndex: 999,
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '6px',
          gap: '16px',
          minWidth: 0
        }}>
          {onNavigate && (
            <button
              onClick={() => onNavigate('home')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '10px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                backdropFilter: 'blur(10px)',
                flexShrink: 0,
                width: '44px',
                height: '44px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={t('dashboard.back_to_home')}
            >
              <Home size={20} />
            </button>
          )}
          <div style={{ 
            fontSize: 'clamp(18px, 4.5vw, 40px)', 
            fontWeight: 700, 
            maxWidth: '100%',
            letterSpacing: '-0.5px',
            lineHeight: 1.2,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            <BarChart3 size={22} style={{ flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {t('dashboard.title')}
            </span>
          </div>
          <div className="dashboard-topbar-mobile" style={{ display: 'none', gap: '12px', alignItems: 'center', flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              aria-label={t('home.open_menu') || t('common.menu') || 'Open menu'}
              title={t('home.open_menu') || t('common.menu') || 'Open menu'}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '10px',
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(10px)',
                width: '44px',
                height: '44px'
              }}
            >
              <PanelRightOpen size={20} aria-hidden />
            </button>
          </div>
          <div className="dashboard-topbar-desktop" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexShrink: 0 }}>
            <LanguageSelector style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', padding: '6px 10px', borderRadius: 10, flexShrink: 0 }} />
            {lastUpdate && (
              <ModernBadge variant="info" style={{ fontSize: '12px' }}>
                <Clock size={12} style={{ marginRight: '4px' }} />
                {t('dashboard.last_update').replace('{{time}}', lastUpdate.toLocaleTimeString(navigator.language || 'fr-FR'))}
              </ModernBadge>
            )}
            <button 
              onClick={handleRefresh} 
              disabled={refreshing}
              style={{
                padding: '10px 20px',
                background: colorPalette.stats.gradient,
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: refreshing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s',
                opacity: refreshing ? 0.7 : 1,
                boxShadow: `0 4px 16px ${colorPalette.stats.shadow}`
              }}
              onMouseEnter={(e) => {
                if (!refreshing) e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <RefreshCw 
                size={16} 
                style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none', display: 'inline-block' }} 
              />
              {refreshing ? t('dashboard.refreshing') : t('dashboard.refresh')}
            </button>
            <button
              type="button"
              onClick={() => reportsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              style={{
                padding: '10px 16px',
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.25)',
                color: '#fff',
                borderRadius: '12px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              title={t('dashboard.generate_reports') || 'Generate Reports'}
            >
              <Download size={16} />
              {t('dashboard.generate_reports') || 'Generate Reports'}
            </button>
          </div>
        </div>
        <p className="dashboard-topbar-subtitle" style={{
          fontSize: 'clamp(14px, 2vw, 16px)',
          color: 'rgba(255, 255, 255, 0.7)',
          margin: '8px 0 0 0',
          letterSpacing: '0.2px',
          fontWeight: 500
        }}>
          {!loading && (isEmployee ? (t('dashboard.subtitle_employee') || 'Real-time data • Your dashboard') : dashboardData.overview?.totalEmployees != null && t('dashboard.subtitle').replace('{{count}}', dashboardData.overview?.totalEmployees || 0))}
        </p>
      </div>

      {/* Drawer mobile: top bar data (subtitle, last update, refresh) */}
      {isMobile && mobileMenuOpen && (
        <div
          role="presentation"
          onClick={() => setMobileMenuOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            zIndex: 2000,
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(340px, 88vw)',
              height: '100%',
              background: 'rgba(15, 23, 42, 0.92)',
              backdropFilter: 'blur(24px)',
              borderLeft: '1px solid rgba(255,255,255,0.12)',
              padding: '18px 16px',
              boxSizing: 'border-box',
              overflowY: 'auto'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <div style={{ fontWeight: 700, fontSize: '16px', letterSpacing: '0.2px', color: '#fff' }}>
                {t('dashboard.title')}
              </div>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                aria-label={t('common.close') || 'Close'}
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.16)',
                  color: '#ffffff',
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <X size={18} />
              </button>
            </div>

            {!loading && (isEmployee || dashboardData.overview?.totalEmployees != null) && (
              <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)', margin: '0 0 16px 0', fontWeight: 500 }}>
                {isEmployee ? (t('dashboard.subtitle_employee') || 'Real-time data • Your dashboard') : t('dashboard.subtitle').replace('{{count}}', dashboardData.overview?.totalEmployees || 0)}
              </p>
            )}

            <div style={{ marginBottom: '16px' }}>
              <LanguageSelector style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                padding: '6px 10px',
                borderRadius: '10px',
                backdropFilter: 'blur(10px)'
              }} />
            </div>

            {lastUpdate && (
              <div style={{ marginBottom: '12px' }}>
                <ModernBadge variant="info" style={{ fontSize: '12px' }}>
                  <Clock size={12} style={{ marginRight: '6px' }} />
                  {t('dashboard.last_update').replace('{{time}}', lastUpdate.toLocaleTimeString(navigator.language || 'fr-FR'))}
                </ModernBadge>
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: '10px' }}>
                {t('dashboard.generate_reports') || 'Generate Reports'}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button type="button" onClick={() => { setMobileMenuOpen(false); handleExportImage(); }} disabled={!!exportingFormat}
                  style={{ padding: '12px 14px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', color: '#fff', borderRadius: '12px', cursor: exportingFormat ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 600 }}>
                  <Image size={18} /> {t('dashboard.report_image') || 'Image'}
                </button>
                <button type="button" onClick={() => { setMobileMenuOpen(false); handleExportExcel(); }} disabled={!!exportingFormat}
                  style={{ padding: '12px 14px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', color: '#fff', borderRadius: '12px', cursor: exportingFormat ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 600 }}>
                  <FileSpreadsheet size={18} /> {t('dashboard.report_excel') || 'Excel'}
                </button>
                <button type="button" onClick={() => { setMobileMenuOpen(false); handleExportPdf(); }} disabled={!!exportingFormat}
                  style={{ padding: '12px 14px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', color: '#fff', borderRadius: '12px', cursor: exportingFormat ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 600 }}>
                  <FileText size={18} /> {t('dashboard.report_pdf') || 'PDF'}
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={async () => {
                setMobileMenuOpen(false);
                await handleRefresh();
              }}
              disabled={refreshing}
              style={{
                width: '100%',
                padding: '12px 14px',
                background: colorPalette.stats.gradient,
                color: 'white',
                border: 'none',
                borderRadius: '14px',
                fontSize: '14px',
                fontWeight: 700,
                cursor: refreshing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                opacity: refreshing ? 0.7 : 1,
                boxShadow: `0 6px 18px ${colorPalette.stats.shadow}`
              }}
            >
              <RefreshCw
                size={18}
                style={{
                  animation: refreshing ? 'spin 1s linear infinite' : 'none',
                  display: 'inline-block'
                }}
              />
              {refreshing ? t('dashboard.refreshing') : t('dashboard.refresh')}
            </button>
          </div>
        </div>
      )}

      {/* Contenu principal avec padding pour le header */}
      <div className="dashboard-content" style={{
        paddingTop: isMobile ? '80px' : '120px',
        paddingBottom: '40px',
        paddingLeft: '20px',
        paddingRight: '20px',
        maxWidth: '1600px',
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden'
      }}>
        {loading && (isEmployee ? true : !dashboardData.overview?.totalEmployees) ? (
          <ModernCard
            gradient={colorPalette.stats.gradient}
            shadow={colorPalette.stats.shadow}
            style={{ textAlign: 'center', padding: '64px 24px' }}
          >
            <div style={{
              width: '4rem',
              height: '4rem',
              border: '4px solid rgba(255,255,255,0.3)',
              borderTop: '4px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }}></div>
            <p style={{ fontSize: '1.25rem', fontWeight: '500', color: '#ffffff', marginBottom: '8px' }}>
              {t('dashboard.loading')}
            </p>
            <p style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)' }}>
              {t('dashboard.loading_desc')}
            </p>
          </ModernCard>
        ) : (
          <>
          <div ref={dashboardReportRef} style={{ outline: 'none' }}>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {dashboardData.alerts && dashboardData.alerts.length > 0 && (
        <ModernCard
          style={{
            background: dashboardData.alerts[0].type === 'error' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(59, 130, 246, 0.2)',
            border: `1px solid ${dashboardData.alerts[0].type === 'error' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`,
            marginBottom: '24px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: dashboardData.alerts[0].type === 'error' ? '#ef4444' : '#3b82f6' }}>
            <AlertCircle size={20} />
            <span style={{ fontWeight: 600 }}>{t('dashboard.' + dashboardData.alerts[0].message)}</span>
          </div>
        </ModernCard>
      )}

      {/* Onglets modernes */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '24px',
        flexWrap: 'wrap',
        overflowX: 'auto',
        paddingBottom: '8px'
      }}>
        {[
          { id: 'overview', name: t('dashboard.tab_overview'), icon: <BarChart3 size={18} /> },
          ...(!isEmployee ? [{ id: 'departments', name: t('dashboard.tab_departments'), icon: <Building size={18} /> }] : []),
          { id: 'performance', name: t('dashboard.tab_performance'), icon: <TrendingUp size={18} /> },
          ...(hasConges ? [{ id: 'leaves', name: t('dashboard.tab_leaves'), icon: <Calendar size={18} /> }] : [])
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setSelectedView(tab.id)}
            style={{
              padding: '12px 20px',
              background: selectedView === tab.id 
                ? colorPalette.stats.gradient 
                : 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff',
              border: selectedView === tab.id 
                ? 'none' 
                : '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              backdropFilter: 'blur(10px)',
              boxShadow: selectedView === tab.id 
                ? `0 4px 16px ${colorPalette.stats.shadow}` 
                : 'none'
            }}
            onMouseEnter={(e) => {
              if (selectedView !== tab.id) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedView !== tab.id) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }
            }}
          >
            {tab.icon}
            {tab.name}
          </button>
        ))}
      </div>

      {/* Filters: Per Employee (default) / Per Department — admin/manager only */}
      {!isEmployee && (() => {
        const filterByText = (() => { const v = t('dashboard.filter_by'); return (v && !String(v).includes('.')) ? v : 'Filter by'; })();
        const perEmployeeText = (() => { const v = t('dashboard.filter_per_employee'); return (v && !String(v).includes('.')) ? v : 'Per Employee'; })();
        const perDeptText = (() => { const v = t('dashboard.filter_per_department'); return (v && !String(v).includes('.')) ? v : 'Per Department'; })();
        const allDeptsText = (() => { const v = t('dashboard.filter_all_departments'); return (v && !String(v).includes('.')) ? v : 'All departments'; })();
        const allEmployeesText = (() => { const v = t('dashboard.filter_all_employees'); return (v && !String(v).includes('.')) ? v : 'All employees'; })();
        const employees = dashboardData.filterOptions?.employees || [];
        const departments = dashboardData.filterOptions?.departments || [];
        const empLabel = (emp) => [emp.firstName || emp.first_name, emp.lastName || emp.last_name].filter(Boolean).join(' ') || `Employee #${emp.id}`;
        return (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px',
          padding: '12px 16px',
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.12)'
        }}>
          <span style={{ fontSize: '14px', fontWeight: 600, color: 'rgba(255,255,255,0.9)', marginRight: '4px' }}>
            {filterByText}:
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => { setFilterType('employee'); setFilterValue(''); }}
              style={{
                padding: '8px 16px',
                borderRadius: '10px',
                border: filterType === 'employee' ? 'none' : '1px solid rgba(255,255,255,0.3)',
                background: filterType === 'employee' ? colorPalette.stats.gradient : 'transparent',
                color: '#fff',
                fontWeight: 600,
                fontSize: '13px',
                cursor: 'pointer',
                boxShadow: filterType === 'employee' ? `0 2px 8px ${colorPalette.stats.shadow}` : 'none'
              }}
            >
              {perEmployeeText}
            </button>
            <button
              type="button"
              onClick={() => { setFilterType('department'); setFilterValue(''); }}
              style={{
                padding: '8px 16px',
                borderRadius: '10px',
                border: filterType === 'department' ? 'none' : '1px solid rgba(255,255,255,0.3)',
                background: filterType === 'department' ? colorPalette.stats.gradient : 'transparent',
                color: '#fff',
                fontWeight: 600,
                fontSize: '13px',
                cursor: 'pointer',
                boxShadow: filterType === 'department' ? `0 2px 8px ${colorPalette.stats.shadow}` : 'none'
              }}
            >
              {perDeptText}
            </button>
          </div>
          <select
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            style={{
              minWidth: '200px',
              padding: '8px 12px',
              borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.25)',
              background: 'rgba(0,0,0,0.2)',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            {filterType === 'employee' ? (
              <>
                <option value="">{allEmployeesText}</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={String(emp.id)}>
                    {empLabel(emp)}
                  </option>
                ))}
              </>
            ) : (
              <>
                <option value="">{allDeptsText}</option>
                {departments.map((dept) => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </>
            )}
          </select>
        </div>
        );
      })()}

      {/* Cartes de statistiques */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '24px'
      }}>
        {[
          ...(!isEmployee ? [
            { title: t('dashboard.stat_total_employees'), value: dashboardData.overview?.totalEmployees || 0, subtitle: t('dashboard.stat_departments').replace('{{count}}', dashboardData.overview?.totalDepartments || 0), icon: <Users size={32} />, gradient: colorPalette.equipe.gradient, shadow: colorPalette.equipe.shadow },
            { title: t('dashboard.stat_present_today'), value: dashboardData.overview?.presentToday || 0, subtitle: t('dashboard.stat_presence_rate').replace('{{percentage}}', Math.round(((dashboardData.overview?.presentToday || 0) / (dashboardData.overview?.totalEmployees || 1)) * 100)), icon: <CheckCircle size={32} />, gradient: colorPalette.profil.gradient, shadow: colorPalette.profil.shadow }
          ] : []),
          { 
            title: t('dashboard.stat_on_time'), 
            value: isEmployee ? (dashboardData.overview?.onTimeToday ? 1 : 0) : (dashboardData.overview?.onTimeToday || 0), 
            subtitle: isEmployee ? t('dashboard.stat_today') : t('dashboard.stat_punctuality_rate').replace('{{percentage}}', Math.round(((dashboardData.overview?.onTimeToday || 0) / (dashboardData.overview?.presentToday || 1)) * 100)),
            icon: <Clock size={32} />,
            gradient: colorPalette.planning.gradient,
            shadow: colorPalette.planning.shadow
          },
          { 
            title: t('dashboard.stat_avg_arrival'), 
            value: dashboardData.overview?.avgArrivalTime != null ? `${Math.floor(dashboardData.overview.avgArrivalTime)}h${String(Math.round((dashboardData.overview.avgArrivalTime % 1) * 60)).padStart(2, '0')}` : '--', 
            subtitle: t('dashboard.stat_today'),
            icon: <Clock size={32} />,
            gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
            shadow: 'rgba(245, 158, 11, 0.3)'
          },
          { 
            title: t('dashboard.stat_late'), 
            value: (dashboardData.overview?.lateCount != null ? dashboardData.overview.lateCount : dashboardData.lateArrivals?.length) || 0, 
            subtitle: t('dashboard.stat_today'),
            icon: <AlertCircle size={32} />,
            gradient: colorPalette.alertes.gradient,
            shadow: colorPalette.alertes.shadow
          },
          ...(hasConges ? [{ 
            title: t('dashboard.stat_leaves_ongoing'), 
            value: dashboardData.ongoingLeaves ?? (dashboardData.leaveStats?.reduce((sum, leave) => sum + (leave.approved || 0), 0) || 0), 
            subtitle: t('dashboard.stat_today'),
            icon: <Calendar size={32} />,
            gradient: colorPalette.conges.gradient,
            shadow: colorPalette.conges.shadow
          }] : []),
          { 
            title: t('dashboard.stat_gps_attendance'), 
            value: dashboardData.overview?.gpsStats?.totalWithGPS || 0, 
            subtitle: t('dashboard.stat_in_zone').replace('{{count}}', dashboardData.overview?.gpsStats?.withinRadius || 0),
            icon: <Building size={32} />,
            gradient: colorPalette.equipe.gradient,
            shadow: colorPalette.equipe.shadow
          },
          { 
            title: t('dashboard.stat_avg_hours'), 
            value: `${(dashboardData.overview?.avgHoursPerDay ?? 0).toFixed(1)}h`, 
            subtitle: t('dashboard.stat_last_7_days'),
            icon: <Clock size={32} />,
            gradient: colorPalette.messages.gradient,
            shadow: colorPalette.messages.shadow
          }
        ].map((stat, index) => (
          <ModernCard
            key={index}
            gradient={stat.gradient}
            shadow={stat.shadow}
            style={{ textAlign: 'left', cursor: 'default' }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ 
                  fontSize: '13px', 
                  color: 'rgba(255, 255, 255, 0.8)', 
                  fontWeight: 500,
                  marginBottom: '8px'
                }}>
                  {stat.title}
                </div>
                <div style={{ 
                  fontSize: '32px', 
                  fontWeight: 700, 
                  color: '#ffffff',
                  lineHeight: 1,
                  marginBottom: '4px'
                }}>
                  {stat.value}
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontWeight: 500
                }}>
                  {stat.subtitle}
                </div>
              </div>
              <div style={{ 
                opacity: 0.3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {stat.icon}
              </div>
            </div>
          </ModernCard>
        ))}
      </div>

      {/* Contenu selon l'onglet sélectionné */}
      {selectedView === 'overview' && dashboardData.weeklyStats && dashboardData.weeklyStats.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '24px',
          marginBottom: '24px',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box'
        }}>
          {/* Présence hebdomadaire */}
          <ModernCard
            title={t('dashboard.chart_weekly_attendance')}
            icon={<TrendingUp size={20} />}
            style={isMobile ? { width: '100%', maxWidth: '100%', minWidth: 0 } : undefined}
          >
              <div style={mobileStyles.scrollContainer}>
                <div style={mobileStyles.scrollContent}>
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                    <BarChart data={dashboardData.weeklyStats}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="day" stroke="#6b7280" />
                      <YAxis stroke="#6b7280" domain={[0, isEmployee ? 2 : Math.max(2, (dashboardData.overview?.totalEmployees || 0) + 1)]} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb', 
                          borderRadius: '12px',
                          boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                        }} 
                      />
                      <Bar dataKey="present" stackId="a" fill="#10B981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="late" stackId="a" fill="#F59E0B" />
                      <Bar dataKey="absent" stackId="a" fill="#EF4444" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
          </ModernCard>

          {/* Répartition aujourd'hui */}
          <ModernCard
            title={t('dashboard.chart_today_distribution')}
            icon={<BarChart3 size={20} />}
            style={isMobile ? { width: '100%', maxWidth: '100%', minWidth: 0 } : undefined}
          >
              <div style={mobileStyles.chartWrapper}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={dashboardData.attendanceToday || []}
                      cx="50%"
                      cy="50%"
                      innerRadius={isMobile ? 40 : 60}
                      outerRadius={isMobile ? 80 : 100}
                      dataKey="value"
                      stroke="none"
                    >
                      {(dashboardData.attendanceToday || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e5e7eb', 
                        borderRadius: '12px',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                      }} 
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              {/* Légende personnalisée */}
              <div style={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'center', 
                gap: isMobile ? '0.5rem' : '1rem', 
                marginTop: '1rem' 
              }}>
                {(dashboardData.attendanceToday || []).map((entry, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: entry.color,
                      borderRadius: '50%'
                    }}></div>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      {entry.name === 'on_time' ? t('dashboard.chart_legend_on_time') :
                       entry.name === 'present' ? t('dashboard.chart_legend_present') : 
                       entry.name === 'late' ? t('dashboard.chart_legend_late') : 
                       entry.name === 'absent' ? t('dashboard.chart_legend_absent') : entry.name}: {entry.value}
                    </span>
                  </div>
                ))}
              </div>
          </ModernCard>
        </div>
      )}

      {/* Départements */}
      {selectedView === 'departments' && dashboardData.departmentStats && dashboardData.departmentStats.length > 0 && (
        <ModernCard
          title={t('dashboard.section_departments')}
          icon={<Building size={20} />}
        >
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {dashboardData.departmentStats.map((dept, index) => (
                <div key={index} style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '16px',
                  padding: isMobile ? '1rem' : '1.5rem',
                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isMobile) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
                >
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between', 
                    alignItems: isMobile ? 'center' : 'center', 
                    marginBottom: '1rem',
                    textAlign: isMobile ? 'center' : 'left',
                    gap: isMobile ? '0.5rem' : '0'
                  }}>
                    <h4 style={{ margin: 0, fontSize: isMobile ? '1rem' : '1.125rem', fontWeight: '600', color: '#1f2937' }}>
                      {dept.department ?? dept.name ?? '—'}
                    </h4>
                    <span style={{ 
                      fontSize: '0.875rem', 
                      fontWeight: '500', 
                      color: '#6b7280',
                      background: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '50px'
                    }}>
                      {t('dashboard.department_employees').replace('{{count}}', String(dept.total_employees ?? dept.employees ?? 0))}
                    </span>
                  </div>
                  
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', 
                    gap: '1rem', 
                    fontSize: '0.875rem', 
                    marginBottom: '1rem' 
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#10b981', fontSize: isMobile ? '1.2rem' : '1.5rem', fontWeight: 'bold' }}>{dept.present_today ?? 0}</div>
                      <div style={{ color: '#6b7280' }}>{t('dashboard.department_present')}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#f59e0b', fontSize: isMobile ? '1.2rem' : '1.5rem', fontWeight: 'bold' }}>{dept.late_today ?? 0}</div>
                      <div style={{ color: '#6b7280' }}>{t('dashboard.department_late')}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#3b82f6', fontSize: isMobile ? '1.2rem' : '1.5rem', fontWeight: 'bold' }}>{(dept.attendance_rate ?? dept.efficiency ?? 0)}%</div>
                      <div style={{ color: '#6b7280' }}>{t('dashboard.department_attendance')}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#8b5cf6', fontSize: isMobile ? '1.2rem' : '1.5rem', fontWeight: 'bold' }}>{Number(dept.avg_daily_hours ?? dept.avgHours ?? 0).toFixed(1)}h</div>
                      <div style={{ color: '#6b7280' }}>{t('dashboard.department_avg_hours')}</div>
                    </div>
                  </div>
                  
                  <div style={{ position: 'relative' }}>
                    <div style={{ 
                      width: '100%', 
                      height: '8px', 
                      backgroundColor: '#e5e7eb', 
                      borderRadius: '50px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                        borderRadius: '50px',
                        width: `${Math.min(dept.attendance_rate ?? dept.efficiency ?? 0, 100)}%`,
                        transition: 'width 1s ease'
                      }}></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ModernCard>
        )}

      {/* Performance */}
      {selectedView === 'performance' && dashboardData.monthlyTrends && dashboardData.monthlyTrends.length > 0 && (
        <ModernCard
          title={t('dashboard.chart_monthly_trends')}
          icon={<TrendingUp size={20} />}
        >
            {isMobile && (
              <div style={{
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '8px',
                padding: '8px',
                marginBottom: '10px',
                textAlign: 'center',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                color: '#3B82F6'
              }}>
                {t('dashboard.chart_scroll_hint_data')}
              </div>
            )}
            <div style={mobileStyles.scrollContainer}>
              <div style={mobileStyles.scrollContent}>
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={dashboardData.monthlyTrends}>
                    <defs>
                      <linearGradient id="colorAttendance" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorProductivity" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorSatisfaction" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e5e7eb', 
                        borderRadius: '12px',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                      }} 
                    />
                    <Area type="monotone" dataKey="attendance" stroke="#3b82f6" fillOpacity={1} fill="url(#colorAttendance)" strokeWidth={3} />
                    <Area type="monotone" dataKey="productivity" stroke="#10b981" fillOpacity={1} fill="url(#colorProductivity)" strokeWidth={3} />
                    <Area type="monotone" dataKey="satisfaction" stroke="#8b5cf6" fillOpacity={1} fill="url(#colorSatisfaction)" strokeWidth={3} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </ModernCard>
        )}

      {/* Congés - only for Business/Enterprise (hasConges). Always show card; data is real-time (today) and respects Per Employee / Per Department filter. */}
      {hasConges && selectedView === 'leaves' && (
        <ModernCard
          title={t('dashboard.section_leaves')}
          icon={<Calendar size={20} />}
        >
            <p style={{ margin: '0 0 12px 0', fontSize: '0.8rem', color: 'rgba(255,255,255,0.75)' }}>
              {t('dashboard.leaves_chart_subtitle')}
            </p>
            {isMobile && (
              <div style={{
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '8px',
                padding: '8px',
                marginBottom: '10px',
                textAlign: 'center',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                color: '#3B82F6'
              }}>
                {t('dashboard.chart_scroll_hint_data')}
              </div>
            )}
            {(!dashboardData.leaveStats || dashboardData.leaveStats.length === 0) ? (
              <div style={{
                padding: '2rem',
                textAlign: 'center',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '12px',
                border: '1px dashed rgba(255,255,255,0.2)',
                color: 'rgba(255,255,255,0.7)',
                fontSize: '0.9rem'
              }}>
                {t('dashboard.leaves_empty')}
              </div>
            ) : (
            <div style={{
              ...mobileStyles.scrollContainer,
              ...(isMobile ? { overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch', paddingBottom: '8px' } : {})
            }}>
              <div style={{
                ...mobileStyles.scrollContent,
                ...(isMobile ? { minWidth: `${Math.max(100, (dashboardData.leaveStats?.length || 3) * 100)}px` } : {})
              }}>
                <div style={{
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: '16px',
                  border: '1px solid rgba(255,255,255,0.08)',
                  padding: '20px 16px 12px',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
                  ...(isMobile ? { minWidth: `${(dashboardData.leaveStats?.length || 3) * 100}px` } : {}),
                }}>
                  <ResponsiveContainer
                    width={isMobile ? Math.max(320, (dashboardData.leaveStats?.length || 3) * 100) : '100%'}
                    height={380}
                  >
                    <BarChart
                      data={dashboardData.leaveStats.map(d => ({
                        ...d,
                        total: (d.approved || 0) + (d.pending || 0) + (d.rejected || 0)
                      }))}
                      margin={{ top: 16, right: 16, left: 38, bottom: isMobile ? 56 : 8 }}
                      barCategoryGap="36%"
                      barGap={8}
                      maxBarSize={48}
                    >
                      <CartesianGrid strokeDasharray="4 4" stroke="rgba(255,255,255,0.06)" vertical={false} />
                      <XAxis
                        dataKey="name"
                        tick={{
                          fill: 'rgba(255,255,255,0.9)',
                          fontSize: isMobile ? 11 : 13,
                          fontWeight: 600,
                          textAnchor: isMobile ? 'end' : 'middle'
                        }}
                        angle={isMobile ? -35 : 0}
                        axisLine={{ stroke: 'rgba(255,255,255,0.12)' }}
                        tickLine={false}
                        dy={isMobile ? 4 : 10}
                        interval={0}
                        width={isMobile ? 72 : undefined}
                        tickMargin={isMobile ? 12 : 8}
                        tickFormatter={isMobile ? (val) => (val && val.length > 12 ? `${val.slice(0, 10)}…` : val) : undefined}
                      />
                      <YAxis
                        allowDecimals={false}
                        domain={[0, (dataMax) => Math.max(1, Math.ceil(dataMax))]}
                        tick={{ fill: 'rgba(255,255,255,0.65)', fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        width={28}
                        tickMargin={8}
                        label={{ value: t('dashboard.leaves_yaxis_label'), angle: -90, position: 'insideLeft', style: { fill: 'rgba(255,255,255,0.7)', fontSize: 11, textAnchor: 'middle' } }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                          padding: '16px 24px',
                          minWidth: '180px'
                        }}
                        cursor={{ fill: 'rgba(255,255,255,0.04)', radius: 8 }}
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0].payload;
                          const approved = Number(d.approved) || 0;
                          const pending = Number(d.pending) || 0;
                          const rejected = Number(d.rejected) || 0;
                          return (
                            <div style={{ padding: '4px 0 4px 16px', backgroundColor: '#ffffff', minWidth: '140px', borderRadius: '8px' }}>
                              <div style={{ color: '#000000', fontWeight: 600, marginBottom: 12, fontSize: 14 }}>
                                {d.name}
                              </div>
                              <div style={{ color: '#16a34a', fontSize: 13 }}>approved : {approved}</div>
                              <div style={{ color: '#ea580c', fontSize: 13 }}>pending : {pending}</div>
                              <div style={{ color: '#dc2626', fontSize: 13 }}>rejected : {rejected}</div>
                            </div>
                          );
                        }}
                      />
                      <Bar dataKey="total" name="total" radius={[6, 6, 0, 0]} maxBarSize={48}>
                        {(() => {
                          const LEAVE_TYPE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#14b8a6'];
                          return (dashboardData.leaveStats || []).map((entry, index) => (
                            <Cell key={entry.name || index} fill={LEAVE_TYPE_COLORS[index % LEAVE_TYPE_COLORS.length]} />
                          ));
                        })()}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            )}
          </ModernCard>
        )}

      {/* Alertes et informations */}
      <ModernCard
        title={t('dashboard.section_notifications')}
        icon={<AlertCircle size={20} />}
      >
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '1rem' 
          }}>
            {/* Alerte temps réel */}
            <div style={{
              background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
              border: '1px solid #3b82f6',
              borderRadius: '12px',
              padding: '1rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
                <span style={{ fontSize: '1.25rem' }}>ℹ️</span>
                <div>
                  <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#1e40af' }}>
                    {t('dashboard.alert_connected')}
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: '#3730a3' }}>
                    {t('dashboard.alert_connected_desc')}
                  </p>
                </div>
              </div>
            </div>

            {/* Retards du jour */}
            {(dashboardData.lateArrivals?.length || 0) > 0 && (
              <div style={{
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                border: '1px solid #f59e0b',
                borderRadius: '12px',
                padding: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1.25rem' }}>⚠️</span>
                  <div>
                    <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#92400e' }}>
                      {t('dashboard.alert_late_detected')}
                    </h4>
                    <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.75rem', color: '#a16207' }}>
                      {t('dashboard.alert_late_count').replace('{{count}}', dashboardData.lateArrivals.length)}
                    </p>
                    <div style={{ fontSize: '0.6875rem', color: '#78716c' }}>
                      {dashboardData.lateArrivals.map((late, i) => {
                        const name = late.name != null && String(late.name).trim() !== '' ? late.name : 'Employé';
                        const time = late.arrival_time != null ? String(late.arrival_time).substring(0, 5) : '--:--';
                        return <div key={i}>• {name} ({time})</div>;
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Performance globale (admin/manager only) */}
            {!isEmployee && (
            <div style={{
              background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
              border: '1px solid #10b981',
              borderRadius: '12px',
              padding: '1rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
                <span style={{ fontSize: '1.25rem' }}>📊</span>
                <div>
                  <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#065f46' }}>
                    {t('dashboard.alert_global_performance')}
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: '#047857' }}>
                    {t('dashboard.alert_performance_today').replace('{{percentage}}', Math.round(((dashboardData.overview?.presentToday || 0) / (dashboardData.overview?.totalEmployees || 1)) * 100))}
                  </p>
                </div>
              </div>
            </div>
            )}

            {/* Statistiques GPS */}
            {dashboardData.overview?.gpsStats && dashboardData.overview.gpsStats.totalWithGPS > 0 && (
              <div style={{
                background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                border: '1px solid #3b82f6',
                borderRadius: '12px',
                padding: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1.25rem' }}>🌍</span>
                  <div>
                    <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#1e40af' }}>
                      {t('dashboard.alert_gps_today')}
                    </h4>
                    <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.75rem', color: '#3730a3' }}>
                      {t('dashboard.alert_in_zone').replace('{{count}}', dashboardData.overview.gpsStats.withinRadius)}
                    </p>
                    {dashboardData.overview.gpsStats.outsideRadius > 0 && (
                      <p style={{ margin: 0, fontSize: '0.75rem', color: '#3730a3' }}>
                        {t('dashboard.alert_out_zone').replace('{{count}}', dashboardData.overview.gpsStats.outsideRadius).replace('{{distance}}', (dashboardData.overview.gpsStats.avgDistance?.toFixed(0) || 0))}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Statistiques Reconnaissance Faciale */}
            {dashboardData.overview?.gpsStats && dashboardData.overview.gpsStats.withFaceDetection > 0 && (
              <div style={{
                background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
                border: '1px solid #8b5cf6',
                borderRadius: '12px',
                padding: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1.25rem' }}>📸</span>
                  <div>
                    <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#6b21a8' }}>
                      {t('dashboard.alert_face_recognition')}
                    </h4>
                    <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.75rem', color: '#7e22ce' }}>
                      {t('dashboard.alert_face_detection').replace('{{count}}', dashboardData.overview.gpsStats.withFaceDetection)}
                    </p>
                    {dashboardData.overview.gpsStats.manualOverrides > 0 && (
                      <p style={{ margin: 0, fontSize: '0.75rem', color: '#7e22ce' }}>
                        {t('dashboard.alert_manual_overrides').replace('{{count}}', dashboardData.overview.gpsStats.manualOverrides)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
      </ModernCard>

      {/* Styles pour l'animation de rotation */}
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          /* Scrollbars personnalisées pour mobile */
          @media (max-width: 768px) {
            *::-webkit-scrollbar {
              height: 8px;
            }
            
            *::-webkit-scrollbar-track {
              background: #f1f5f9;
              border-radius: 4px;
            }
            
            *::-webkit-scrollbar-thumb {
              background: #3b82f6;
              border-radius: 4px;
              border: 2px solid white;
            }
            
            *::-webkit-scrollbar-thumb:hover {
              background: #2563eb;
            }
          }
        `}</style>
          </div>

      {/* Generate Reports — outside report ref so NOT in image export; for all roles including employee */}
      <div ref={reportsSectionRef}>
      <ModernCard style={{ marginTop: '24px', marginBottom: '24px', padding: '20px' }}>
        <h3 style={{
          fontSize: 'clamp(16px, 2.5vw, 18px)',
          fontWeight: 700,
          color: '#ffffff',
          margin: '0 0 16px 0',
          letterSpacing: '0.3px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <Download size={20} />
          {t('dashboard.generate_reports') || 'Generate Reports'}
        </h3>
        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.75)', margin: '0 0 16px 0', lineHeight: 1.4 }}>
          {isEmployee
            ? (t('dashboard.generate_reports_desc_employee') || 'Export your dashboard in one of the formats below.')
            : (t('dashboard.generate_reports_desc') || 'Export the current dashboard view (selected tab & filters) in one of the formats below.')}
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '12px',
          alignItems: 'stretch'
        }}>
          <button
            type="button"
            onClick={handleExportImage}
            disabled={!!exportingFormat}
            title={t('dashboard.report_image') || 'Download as Image'}
            style={{
              padding: '14px 16px',
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.22)',
              color: '#fff',
              borderRadius: '12px',
              cursor: exportingFormat ? 'not-allowed' : 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: 600,
              transition: 'all 0.2s'
            }}
          >
            <Image size={24} />
            {t('dashboard.report_image') || 'Image'}
          </button>
          <button
            type="button"
            onClick={handleExportExcel}
            disabled={!!exportingFormat}
            title={t('dashboard.report_excel') || 'Download as Excel'}
            style={{
              padding: '14px 16px',
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.22)',
              color: '#fff',
              borderRadius: '12px',
              cursor: exportingFormat ? 'not-allowed' : 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: 600,
              transition: 'all 0.2s'
            }}
          >
            <FileSpreadsheet size={24} />
            {t('dashboard.report_excel') || 'Excel'}
          </button>
          <button
            type="button"
            onClick={handleExportPdf}
            disabled={!!exportingFormat}
            title={t('dashboard.report_pdf') || 'Download as PDF'}
            style={{
              padding: '14px 16px',
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.22)',
              color: '#fff',
              borderRadius: '12px',
              cursor: exportingFormat ? 'not-allowed' : 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: 600,
              transition: 'all 0.2s'
            }}
          >
            <FileText size={24} />
            {t('dashboard.report_pdf') || 'PDF'}
          </button>
        </div>
      </ModernCard>
      </div>
          </>
        )}
      </div>

      {/* CSS pour animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (min-width: 768px) {
          .dashboard-content {
            padding: 140px 40px 40px !important;
          }
        }
        @media (min-width: 1024px) {
          .dashboard-content {
            padding: 160px 60px 60px !important;
          }
        }
        .dashboard-content {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard;